import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe, Clock, CheckCircle2, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { EstdBadge } from "@/components/site/EstdBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IMAGES, CONTACT, SERVICES } from "@/lib/data";

const fieldClass = "rounded-none border-0 border-b-2 border-charcoal/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-maroon text-charcoal text-base h-12";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "", service: "", location: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile) {
      toast.error("Please fill in your name, mobile and email.");
      return;
    }
    toast.success("Enquiry received — thank you! We'll be in touch shortly.");
    setSubmitted(true);
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us — Get an Advertising Quote" description="Contact Twins Media in Manipal, Karnataka for railway station advertising, digital displays, hoardings and branding campaigns. Call +91 99805 49123 or send an enquiry." path="/contact" />
      <section className="relative pt-40 md:pt-52 pb-16 md:pb-20 overflow-hidden bg-maroon text-cream">
        <div className="absolute inset-0 -z-10 opacity-20">
          <img src={IMAGES.heroRailway} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-maroon/70" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold"><span className="h-px w-8 bg-gold" />Contact Twins Media</span>
          <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight">
            <MaskLines lines={["Let's Discuss", <span key="s" className="italic text-gold">Your Campaign</span>]} start={0.1} />
          </h1>
          <p className="mt-6 text-cream/80 text-lg max-w-2xl leading-relaxed">
            Whether you're looking for railway station advertising, digital displays, hoardings, or a customized branding campaign, our team is ready to assist you.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <EstdBadge className="w-24 h-24 md:w-28 md:h-28 mb-8" />
              <div className="space-y-10">
                <div>
                  <div className="flex items-center gap-3 text-maroon mb-4"><MapPin size={18} className="text-gold" /><span className="text-xs uppercase tracking-[0.25em]">Office Address</span></div>
                  <address className="not-italic font-display text-2xl md:text-3xl text-charcoal leading-snug">
                    {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
                  </address>
                </div>

                <div className="grid sm:grid-cols-2 gap-8 pt-8 border-t border-charcoal/10">
                  <div>
                    <div className="flex items-center gap-3 text-maroon mb-3"><Phone size={16} className="text-gold" /><span className="text-xs uppercase tracking-[0.25em]">Phone</span></div>
                    <a href="tel:+919980549123" className="text-lg text-charcoal hover:text-maroon transition-colors" data-testid="contact-phone">{CONTACT.phone}</a>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 text-maroon mb-3"><Mail size={16} className="text-gold" /><span className="text-xs uppercase tracking-[0.25em]">Email</span></div>
                    <a href={`mailto:${CONTACT.email}`} className="text-lg text-charcoal hover:text-maroon transition-colors break-all" data-testid="contact-email">{CONTACT.email}</a>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 text-maroon mb-3"><Globe size={16} className="text-gold" /><span className="text-xs uppercase tracking-[0.25em]">Website</span></div>
                    <span className="text-lg text-charcoal">{CONTACT.website}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 text-maroon mb-3"><Clock size={16} className="text-gold" /><span className="text-xs uppercase tracking-[0.25em]">Business Hours</span></div>
                    {CONTACT.hours.map((h) => (
                      <p key={h.day} className="text-charcoal"><span className="block text-sm text-charcoal-muted">{h.day}</span>{h.time}</p>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-paper p-8 md:p-12 border border-charcoal/10 min-h-[560px] flex flex-col justify-center">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="thanks"
                    data-testid="thank-you-message"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-10"
                  >
                    <CheckCircle2 size={56} className="text-maroon mx-auto" />
                    <h2 className="mt-6 font-display text-4xl md:text-5xl text-charcoal">Thank You!</h2>
                    <p className="mt-4 text-charcoal-muted text-lg max-w-md mx-auto">
                      Your enquiry has been received. Our team at Twins Media will get back to you shortly to discuss your advertising requirements.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: "", company: "", mobile: "", email: "", service: "", location: "", message: "" }); }}
                      data-testid="send-another-btn"
                      className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-maroon hover:gap-4 transition-all duration-300"
                    >
                      Send Another Enquiry <ArrowUpRight size={16} />
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    data-testid="enquiry-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-7"
                  >
                    <div>
                      <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-maroon"><span className="h-px w-6 bg-maroon" />Send an Enquiry</span>
                      <h2 className="mt-4 font-display text-3xl md:text-4xl text-charcoal">Tell us about your goals</h2>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Full Name *</Label>
                        <Input data-testid="input-name" value={form.name} onChange={set("name")} className={fieldClass} placeholder="Your name" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Company Name</Label>
                        <Input data-testid="input-company" value={form.company} onChange={set("company")} className={fieldClass} placeholder="Company" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Mobile Number *</Label>
                        <Input data-testid="input-mobile" value={form.mobile} onChange={set("mobile")} className={fieldClass} placeholder="+91 ..." />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Email Address *</Label>
                        <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} className={fieldClass} placeholder="you@email.com" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Service Required</Label>
                        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                          <SelectTrigger data-testid="select-service" className={`${fieldClass} justify-between`}>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICES.map((s) => <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Preferred Location</Label>
                        <Input data-testid="input-location" value={form.location} onChange={set("location")} className={fieldClass} placeholder="City / station" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-charcoal-muted">Message</Label>
                      <Textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={3} className="rounded-none border-0 border-b-2 border-charcoal/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-maroon resize-none" placeholder="Tell us about your campaign..." />
                    </div>

                    <button
                      type="submit"
                      data-testid="submit-enquiry-btn"
                      className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-maroon text-cream px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-maroon-hover transition-colors duration-300"
                    >
                      Submit Enquiry
                      <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28 bg-cream" data-testid="map-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-3 text-maroon mb-6">
            <MapPin size={18} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.25em]">Find Us in Manipal</span>
          </div>
          <div className="border border-charcoal/10 overflow-hidden">
            <iframe
              title="Twins Media office location, Manipal"
              data-testid="google-map"
              src="https://www.google.com/maps?q=Parijatha%2C%20HUDCO%20Colony%2C%20Manipal%2C%20Udupi%2C%20Karnataka%20576104&output=embed"
              className="w-full h-[380px] md:h-[460px] grayscale-[0.15] contrast-[1.05]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Parijatha%2C+HUDCO+Colony%2C+Manipal%2C+Udupi%2C+Karnataka+576104"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="directions-link"
            className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-maroon hover:gap-4 transition-all duration-300"
          >
            Get Directions <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
}
